// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import Pikaday from 'pikaday';

import {formatDate, getCurrentDate, normalizeDateRange, parseDate} from 'common/util/date';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';
import {LocalizationStore} from 'common/i18n/store';
import {i18next} from 'common/i18n';

import {template} from './template';
import css from './index.inline.styl';
import {INCOMPLETE_RANGE_CLASS, NO_MAX_DATE_VALUE} from './types';

const PICKER_ATTRS = new Set([`is-range`, `selected-date`, `selected-from-date`, `selected-to-date`]);

function oneNonEmptyString(a: string, b: string): boolean {
  const aAsBool = Boolean(a);
  const bAsBool = Boolean(b);

  return (!aAsBool && bAsBool) || (!!aAsBool && !bAsBool);
}

interface CalendarJsxProps {
  attrs: CalendarAttrs;
  on?: {
    selectDate: (ev: CustomEvent<{to: string; from: string} | {date: string}>) => void;
  };
}

export interface CalendarAttrs {
  'is-double-calendar'?: boolean;
  'is-from-date-focused'?: boolean;
  'is-range'?: boolean;
  'is-to-date-focused'?: boolean;
  'min-date'?: string;
  'max-date'?: string;
  'selected-date'?: string;
  'selected-from-date'?: string;
  'selected-to-date'?: string;
}

export class Calendar extends Component<EmptyObj, CalendarAttrs, unknown, unknown, IronContextRegistry> {
  private declare contentEl: HTMLDivElement;
  private declare ignoreAttributeChanges: boolean;
  private declare picker: TODO;

  static get attrsSchema(): AttrsSchema<CalendarAttrs> {
    return {
      ...super.attrsSchema,
      'is-double-calendar': `boolean`,
      'is-from-date-focused': `boolean`,
      'is-range': `boolean`,
      'is-to-date-focused': `boolean`,
      'min-date': `string`,
      'max-date': {
        type: `string`,
        description: `set "${NO_MAX_DATE_VALUE}" to allow picking future dates`,
      },
      'selected-date': `string`,
      'selected-from-date': `string`,
      'selected-to-date': `string`,
    };
  }

  get config() {
    return {
      css,
      template,
      contexts: [`i18n` as const],
      defaultContexts: {
        i18n: new LocalizationStore(i18next),
      },
      useShadowDom: true,
      // updateSync is needed since we attach the pikaday calendar to this component in the connectedCallback, so
      // we need the DOM updated synchronously in the connectedCallback.
      updateSync: true,
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  attributeChangedCallback(attr: string, oldVal: string, newVal: string) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (!this.initialized || this.ignoreAttributeChanges) {
      return;
    }

    if (PICKER_ATTRS.has(attr)) {
      this.updatePicker();
    }
  }

  get isDoubleCalendar() {
    return this.attr(`is-double-calendar`);
  }

  get isRange() {
    return this.attr(`is-range`);
  }

  getDateAttribute(name: keyof CalendarAttrs) {
    const date = this.attr(name);
    if (date && date !== `null` && date !== true) {
      if (this.parseDate(date)) {
        return date;
      }
      throw new Error(`${name} is set to invalid value ${date}`);
    } else {
      return null;
    }
  }

  setDateAttribute(name, date) {
    if (date) {
      this.setAttribute(name, date);
    } else {
      this.removeAttribute(name);
    }
  }

  get minDate() {
    return this.getDateAttribute(`min-date`);
  }

  get maxDate() {
    const isUnlimitedMaxDate = this.attr(`max-date`) === NO_MAX_DATE_VALUE;

    return isUnlimitedMaxDate ? null : this.getDateAttribute(`max-date`) || this.getFormattedCurrentDate();
  }

  get selectedDate() {
    return this.getDateAttribute(`selected-date`);
  }

  set selectedDate(date) {
    this.setDateAttribute(`selected-date`, date);
  }

  get selectedFromDate() {
    return this.getDateAttribute(`selected-from-date`);
  }

  set selectedFromDate(date) {
    this.setDateAttribute(`selected-from-date`, date);
  }

  get selectedToDate() {
    return this.getDateAttribute(`selected-to-date`);
  }

  set selectedToDate(date) {
    this.setDateAttribute(`selected-to-date`, date);
  }

  get isFromDateFocused() {
    return this.attr(`is-from-date-focused`);
  }

  get isToDateFocused() {
    return this.attr(`is-to-date-focused`);
  }

  get i18n() {
    const i18n = this.getContext(`i18n`);

    const months = new Array(12).fill(undefined).map((_, m) => new Date(2001, m, 1));
    const week = new Array(7).fill(undefined).map((_, d) => new Date(2001, 0, d));

    return {
      previousMonth: i18n.t(`components.calendar.prev-month`),
      nextMonth: i18n.t(`components.calendar.next-month`),
      months: months.map((date) => i18n.formatDate(date, {month: `long`})),
      weekdays: week.map((date) => i18n.formatDate(date, {weekday: `long`})),
      weekdaysShort: week.map((date) => i18n.formatDate(date, {weekday: `short`})),
    };
  }

  init() {
    this.contentEl = this.el.querySelector<HTMLDivElement>(`.calendar`);
    this.contentEl.innerHTML = ``;

    this.picker = new Pikaday({
      bound: false,
      // HACK: Set events to disabled days in the future gives these days a specific class to indicate they are
      // events. Doing this allows targeting disabled days in the past and future separately to give them different
      // styles.
      events: this.getFutureDateObjs(),
      mainCalendar: `right`,
      maxDate: this.maxDate ? this.parseDate(this.maxDate) : null,
      minDate: this.minDate ? this.parseDate(this.minDate) : null,
      numberOfMonths: this.isDoubleCalendar ? 2 : 1,
      i18n: this.i18n,
      showDaysInNextAndPreviousMonths: false,
      theme: `pika-mixpanel`,
      yearRange: 10,
      onSelect: (date) => this.handleSelectDate(date),
      onDraw: () => this.handleDraw(),
    });

    // Pikaday registers a keydown event handler on the document (?#&!), get rid of it
    document.removeEventListener(`keydown`, this.picker._onKeyChange);

    this.contentEl.appendChild(this.picker.el);

    let gotoDate = null;
    if (!this.isRange) {
      if (this.selectedDate) {
        gotoDate = this.selectedDate;
      }
    } else {
      if (this.selectedToDate) {
        gotoDate = this.selectedToDate;
      } else if (this.selectedFromDate) {
        gotoDate = this.selectedFromDate;
      }
    }
    gotoDate = gotoDate || this.maxDate || this.getFormattedCurrentDate();
    this.picker.gotoDate(this.parseDate(gotoDate));

    this.updatePicker();
  }

  getFutureDateObjs() {
    if (!this.maxDate) {
      return [];
    }

    const futureDates = [];
    const maxDate = this.parseDate(this.maxDate);
    let date = maxDate.getDate();
    const thisMonth = maxDate.getMonth();
    const thisYear = maxDate.getFullYear();
    let newDate = new Date(thisYear, thisMonth, date);
    while (newDate.getMonth() === thisMonth) {
      futureDates.push(newDate.toDateString());
      date++;
      newDate = new Date(thisYear, thisMonth, date);
    }
    return futureDates;
  }

  updatePicker() {
    if (!this.picker) {
      return;
    }

    if (!this.isRange) {
      this.picker.setStartRange(null);
      this.picker.setEndRange(null);
      this.picker.setDate(this.selectedDate ? this.parseDate(this.selectedDate) : null, /* preventOnSelect= */ true);
    } else {
      this.picker.setDate(null);
      this.picker.setStartRange(this.selectedFromDate ? this.parseDate(this.selectedFromDate) : null);
      this.picker.setEndRange(this.selectedToDate ? this.parseDate(this.selectedToDate) : null);
    }

    if (oneNonEmptyString(this.selectedFromDate, this.selectedToDate)) {
      this.picker.el.classList.add(INCOMPLETE_RANGE_CLASS);
    } else {
      this.picker.el.classList.remove(INCOMPLETE_RANGE_CLASS);
    }

    this.picker.draw();
  }

  formatDate(dateObj) {
    const date = formatDate(dateObj, {iso: true});
    if (!date) {
      throw new Error(`Could not formatDate(${dateObj})`);
    }
    return date;
  }

  parseDate(date) {
    const dateObj = parseDate(date);
    if (!dateObj) {
      throw new Error(`Could not parseDate(${date})`);
    }
    return dateObj;
  }

  handleSelectDate(dateObj: Date) {
    this.ignoreAttributeChanges = true;

    const date = this.formatDate(dateObj);
    const oldDate = this.selectedDate;
    const oldFrom = this.selectedFromDate;
    const oldTo = this.selectedToDate;

    if (!this.isRange) {
      this.selectedDate = date;
    } else {
      if (this.isFromDateFocused || this.isToDateFocused) {
        if (this.isFromDateFocused) {
          this.selectedFromDate = date;
          [this.selectedFromDate, this.selectedToDate] = normalizeDateRange(
            this.selectedFromDate,
            this.selectedToDate,
            `from`,
          );
        } else {
          this.selectedToDate = date;
          [this.selectedFromDate, this.selectedToDate] = normalizeDateRange(
            this.selectedFromDate,
            this.selectedToDate,
            `to`,
          );
        }
      } else {
        if (!this.selectedFromDate) {
          this.selectedFromDate = date;
        } else if (!this.selectedToDate) {
          this.selectedToDate = date;
        } else {
          this.selectedFromDate = date;
          this.selectedToDate = null;
        }

        if (
          this.selectedFromDate &&
          this.selectedToDate &&
          this.parseDate(this.selectedFromDate) > this.parseDate(this.selectedToDate)
        ) {
          [this.selectedFromDate, this.selectedToDate] = [this.selectedToDate, this.selectedFromDate];
        }
      }
    }

    if (this.selectedDate !== oldDate || this.selectedFromDate !== oldFrom || this.selectedToDate !== oldTo) {
      this.updatePicker();
    }

    this.emitSelectDate();

    this.ignoreAttributeChanges = false;
  }

  emitSelectDate() {
    const detail: Obj = {};
    if (this.isRange) {
      detail.from = this.selectedFromDate;
      detail.to = this.selectedToDate;
    } else {
      detail.date = this.selectedDate;
    }
    this.dispatchEvent(new CustomEvent(`selectDate`, {detail}));
  }

  handleDraw() {
    if (!this.picker) {
      // Pikaday will trigger this callback in its constructor, before this.picker has been initialized.
      return;
    }

    this.addPickerRowClasses();
    this.dispatchEvent(new CustomEvent(`draw`));
  }

  // For each tr, if it has a child td.is-(start|end|in)range give it the class .has-(start|end|in)range
  // Used to handle edge cases around highlighting cells in the month grid where days are not shown
  // because they are from previous or following months. We still want to highlight these cells
  // according to the date range that is selected.
  addPickerRowClasses() {
    for (const row of Array.from<HTMLTableRowElement>(this.picker.el.getElementsByTagName(`tr`))) {
      for (const type of [`start`, `end`, `in`]) {
        if (Array.from(row.childNodes).some((node: Element) => node.classList.contains(`is-${type}range`))) {
          row.classList.add(`has-${type}range`);
        }
      }
    }
  }

  getFormattedCurrentDate() {
    return this.formatDate(getCurrentDate());
  }
}

export const CalendarEl = defineAndMakeJsxFactory<CalendarJsxProps>(`mp-calendar`, Calendar);
export const CalendarReact = panel2react<CalendarJsxProps>(`mp-calendar`, Calendar);
